import React, { ReactElement } from 'react';
import { RenderElement } from 'types/components';

type TranslationPlaceholderUtilityOptions = {
  renderAsText?: boolean;
};

export function replaceTranslationPlaceholder(
  translation: string | undefined,
  replaceValues: Record<string, RenderElement | string>,
  options?: TranslationPlaceholderUtilityOptions
): RenderElement {
  if (!translation) return null;

  const translationSegments = translation.split(/(\{[^}]+\})/g);

  const renderElements = translationSegments.map((segment, i) => {
    const placeholder = segment.match(/^\{([^}]+)\}$/);

    if (!placeholder) return segment;

    const placeholderName = placeholder[1];
    const replaceValue = replaceValues[placeholderName];

    return replaceValue !== undefined && replaceValue !== null ? (
      React.isValidElement(replaceValue) ? (
        <span key={i} style={{ display: 'contents' }}>
          {replaceValue}
        </span>
      ) : (
        String(replaceValue)
      )
    ) : (
      segment
    );
  });

  return options?.renderAsText ? (
    renderElements.join('')
  ) : (
    <>
      {renderElements.map((element, index) => (
        <React.Fragment key={index}>{element}</React.Fragment>
      ))}
    </>
  );
}

type ExtractPlaceholderValueFn = (val: string, key: string) => RenderElement;

export function replaceTranslationDynamicPlaceholder(
  translation: string,
  replaceValues: Record<
    string,
    ExtractPlaceholderValueFn | RenderElement | string
  >,
  options?: TranslationPlaceholderUtilityOptions
): RenderElement {
  if (!translation) return null;

  const translationSegments = translation.split(/(\{[^}]+\})/g);

  const renderElements = translationSegments.map((segment) => {
    const placeholder = segment.match(/^\{([^}]+)\}$/);

    if (!placeholder) return segment;

    const placeholderNameAndValue = placeholder[1];
    const [placeholderName, placeholderValue] =
      placeholderNameAndValue.split(':');
    const replaceMethod = replaceValues[placeholderName];

    if (typeof replaceMethod === 'function') {
      const resolvedValue = replaceMethod(placeholderValue, placeholderName);
      return options?.renderAsText && React.isValidElement(resolvedValue)
        ? (resolvedValue as ReactElement).props.children
        : resolvedValue;
    }

    return replaceMethod !== undefined && replaceMethod !== null
      ? String(replaceMethod)
      : segment;
  });

  return options?.renderAsText ? (
    renderElements.join('')
  ) : (
    <>
      {renderElements.map((element, index) => (
        <React.Fragment key={index}>{element}</React.Fragment>
      ))}
    </>
  );
}
